import { AddSongFacts, AddSongStep } from 'fym-shared/src/music24/catalogAddSong';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { CatalogItem, CatalogSong } from '../../../../redux/api/fymAPI.types';

/** The steps the stepper shows (design catalog-add-song a0, i): every step but Done. */
export const STEPPER_STEPS: Exclude<AddSongStep, 'done'>[] = ['song', 'release', 'codes', 'cover', 'audio', 'lyrics'];

/** The answer of step 2 (design b1, b2). `out` turns step 3 into the link question. */
export type ReleaseChoice = 'date' | 'noDate' | 'out';

/**
 * How a stepper dot looks: the step on screen (`nowDone` when it already holds its data, for example
 * right after the master upload), a step with its data, a step the user passed with none, or later.
 */
export type StepMark = 'now' | 'nowDone' | 'done' | 'skipped' | 'todo';

/** True for the step on screen, with or without its data. */
export function isCurrentMark(mark: StepMark): boolean {
  return mark === 'now' || mark === 'nowDone';
}

/** True for a step that shows the check. */
export function isCheckedMark(mark: StepMark): boolean {
  return mark === 'done' || mark === 'nowDone';
}

/** The step after `step`. The flow is linear; Done ends it. */
export function stepAfter(step: AddSongStep): AddSongStep {
  const index = STEPPER_STEPS.indexOf(step as Exclude<AddSongStep, 'done'>);
  if (index === -1 || index === STEPPER_STEPS.length - 1) {
    return 'done';
  }
  return STEPPER_STEPS[index + 1] ?? 'done';
}

/** The step before `step`, or undefined on step 1. Back from Done goes to Lyrics. */
export function stepBefore(step: AddSongStep): AddSongStep | undefined {
  if (step === 'done') {
    return 'lyrics';
  }
  const index = STEPPER_STEPS.indexOf(step);
  return index <= 0 ? undefined : STEPPER_STEPS[index - 1];
}

/** True when the row has a cover: an upload, or the cover URL of a claim. */
export function itemHasCover(item: Pick<CatalogItem, 'artworkFile' | 'artworkUrl'>): boolean {
  return item.artworkFile !== null || (item.artworkUrl !== null && item.artworkUrl !== '');
}

/** The facts of the shared rules (fym-shared `catalogAddSong`) for one catalog row. */
export function addSongFactsOf(item: CatalogItem): AddSongFacts {
  const { song } = item;
  return {
    kind: item.kind === CatalogItemKind.unreleased ? 'unreleased' : 'claimed',
    releaseDate: item.releaseDate,
    hasCover: itemHasCover(item),
    hasMaster: song?.hasMaster === true,
    hasLyrics: song?.hasLyrics === true,
  };
}

/** True when a step holds data on the row (a check in the stepper). */
export function stepHasData(step: AddSongStep, item: CatalogItem, song: CatalogSong | undefined): boolean {
  const facts = addSongFactsOf(item);
  switch (step) {
    case 'song':
      return true;
    case 'release':
      return facts.kind === 'claimed' || facts.releaseDate !== null;
    case 'codes':
      return facts.kind === 'claimed' || Boolean(song?.isrc) || Boolean(song?.upc);
    case 'cover':
      return facts.hasCover;
    case 'audio':
      // The song answer has the master as soon as the upload completes; the catalog row follows later.
      return facts.hasMaster || (song?.master ?? null) !== null;
    case 'lyrics':
      return facts.hasLyrics || (song?.lyrics ?? '').trim() !== '';
    case 'done':
      return false;
  }
}

/**
 * The mark of each stepper step (design a1 note): the step on screen is `now` (`nowDone` once it
 * holds its data, so the check shows at once, onboarding review 2026-10-19); a step with data is
 * `done`; a step before the one on screen with no data was skipped; the rest are `todo`. With no
 * row yet only step 1 is `now`.
 */
export function stepMarks(
  current: AddSongStep,
  item: CatalogItem | undefined,
  song: CatalogSong | undefined
): Record<Exclude<AddSongStep, 'done'>, StepMark> {
  const currentIndex = current === 'done' ? STEPPER_STEPS.length : STEPPER_STEPS.indexOf(current);
  const marks = {} as Record<Exclude<AddSongStep, 'done'>, StepMark>;
  STEPPER_STEPS.forEach((step, index) => {
    if (step === current) {
      marks[step] = item !== undefined && stepHasData(step, item, song) ? 'nowDone' : 'now';
    } else if (item !== undefined && stepHasData(step, item, song)) {
      marks[step] = 'done';
    } else if (index < currentIndex) {
      marks[step] = 'skipped';
    } else {
      marks[step] = 'todo';
    }
  });
  return marks;
}

/** The step the page opens: step 1 with no row; else the asked step; else Done. */
export function openingStep(requested: AddSongStep | undefined, hasItem: boolean): AddSongStep {
  if (!hasItem) {
    return 'song';
  }
  return requested ?? 'release';
}

/** `YYYY-MM-DD` of an ISO date, or empty. */
export function dayOf(value: string | null | undefined): string {
  return value === null || value === undefined ? '' : value.slice(0, 10);
}

/** The ISO time the API stores for a `YYYY-MM-DD` day (midnight UTC, as the old form sent it). */
export function isoOfDay(day: string): string {
  return `${day}T00:00:00.000Z`;
}

/** The count of sung lines of a lyrics text (non-empty lines that are not `[Section]` labels). */
export function lyricLineCount(text: string): number {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '' && !/^\[.*\]$/.test(line)).length;
}
